import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getContactStatusSuffix } from '../contacts/contactTypeOptions';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import { useWorkerPairs } from 'components/scheduling/workerPairUtils';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import ContactForm from '../contacts/ContactForm';
import LocationForm from '../contacts/LocationForm';
import { SCHEDULING_COLOR_OPTIONS } from './schedulingConstants';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { useSubcontractorDayPassCheck } from 'services/subcontractors/useSubcontractorDayPassCheck';
import { Division } from '../environments/EnvironmentTypes';
import JobCreationDetailsPanel from '../jobs/JobCreationDetailsPanel';
import PersonForm from '../../views/contacts/PersonForm';
import { addHourToStartTime, getSelectedDate, getSelectedEndTime, getSelectedEquipment, getSelectedResource, getSelectedStartTime } from './functions/addJobOrActivityUtils';

// Rename the name of the function:
const AddJobOrActivityForm: React.FC<{
    viewKey?: string;
    linkedItem?: { [key: string]: any };
}> = ({
    viewKey,
    linkedItem,
}) => {
    const { t } = useTranslation();
    const { userPreferences } = useSettings();
    const { activeFeatures, userDivision } = useAuthContext();

    // Determine if the job creation details side tab may be shown
    const hasJobCreationDetails = useActiveFeature('job_creation_details');

    // Determine whether equipment can be scheduled on the appointment
    const equipmentSchedulingActive = useActiveFeature('equipment_scheduling');

    // Check the subcontractor day passes of the planned assignees before saving
    const { checkDayPasses } = useSubcontractorDayPassCheck();
    const checkPlannedDayPasses = async (data: Record<string, any>): Promise<boolean> => {
        const assigneeHashes: string[] = Array.isArray(data.appointment_assignees) ? data.appointment_assignees : [];
        if (data.context === 'other' || assigneeHashes.length === 0 || !data.appointment_date) return true;
        return checkDayPasses(assigneeHashes.map((userHash) => ({ user_hash: userHash, dates: [data.appointment_date] })));
    };

    // Set the api object
    const apiObject = 'job_or_activity';

    // Get the appointment colors setting
    const { scheduling_color_mode: scheduling_appointment_colors } = userPreferences;

    // Fetch the divisions, to group and restrict the job type options
    const hasDivisionsFeature = activeFeatures.includes('divisions');
    const { response: divisionsResponse } = useFetchData({
        apiUrl: hasDivisionsFeature ? 'get_division_list' : '',
        params: useMemo(() => ({ is_active: true }), []),
    });
    const divisions: Division[] = divisionsResponse?.results ?? [];

    // Get the selected date, start time and resource
    const selectedDate = getSelectedDate(linkedItem);
    const selectedStartTime = getSelectedStartTime(linkedItem);
    const selectedEndTime = getSelectedEndTime(linkedItem);
    const selectedResource = getSelectedResource(linkedItem);
    const selectedEquipment = getSelectedEquipment(linkedItem);

    // Get the worker pairs to offer them as assignee options
    const { workerPairs } = useWorkerPairs();

    // Build the worker pair pseudo options for the assignees multiselect
    const workerPairOptions = useMemo(() => workerPairs.map((pair) => ({
        value: `pair-${pair.id}`,
        full_name: `${pair.foreman.full_name} + ${pair.helper.full_name}`,
        pair
    })), [workerPairs]);

    // Switch the form fields
    const contextField: FieldType[] = [
        { 
            type: 'tabs', 
            name: 'context', 
            defaultValue: 'job', 
            fullWidth: true,
            options: [
                { value: 'job', name: 'scheduling.general.job_context_option' },
                { value: 'other', name: 'scheduling.general.other_context_option' },
            ],
        },
    ];

    // Configure the job fields
    const jobFields: FieldType[] = [
        { 
            type: 'searchselect', 
            name: 'client',
            label: 'job.general.client_label',
            placeholder: 'job.general.client_placeholder',
            objectName: 'client.general.object_name.singular',
            query: {
                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                endpoint: 'get_contact_search',
                params: {
                    contact_type: ['contact', 'lead', 'client'] 
                }
            },
            selectionFormat: 'name',
            selectionSuffix: (option) => getContactStatusSuffix(option),
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                bracket: { field: 'contact_type', format: (value, option) => getContactStatusSuffix(option, value) ?? '', optional: false },
                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
            },
            storeWholeObject: true,
            dependentOn: { fieldName: 'context', value: 'job' },
            addNewItem: { 
                method: 'modal-form', 
                modalForm: <ContactForm />,
                linkedItem: { contact_type: 'client' }, 
                onSuccess: (response, { setUpdatedData }) => {
                    setUpdatedData(prev => {
                        let updated = { ...prev };

                        // Set the newly added person of the organization, as location person
                        if (response.entity_type === 'organization' && response.primary_person) {
                            if (!prev.location_person) {
                                updated.location_person = {
                                    id: response.primary_person.id,
                                    value: response.primary_person.id,
                                    name: response.primary_person.name,
                                    ...response.primary_person
                                };
                            }
                        }

                        // Reset the location person value, if the selected client is a person
                        if (response.entity_type === "person") {
                            updated.location_person = undefined;
                        }

                        // Set the newly added location, as work location
                        if (response.primary_locations && response.primary_locations.length > 0) {
                            if (!prev.work_location) {
                                const newLocation = response.primary_locations[0];
                                updated.work_location = {
                                    id: newLocation.id,
                                    value: newLocation.id,
                                    address: newLocation.address,
                                    ...newLocation
                                };
                            }
                        }

                        return updated;
                    });
                }
            },
        },
        { 
            type: 'searchselect', 
            name: 'work_location',
            label: 'job.general.work_location_label',
            placeholder: 'job.general.work_location_placeholder',
            objectName: 'location.general.object_name.singular',
            query: { endpoint: 'get_location_search' },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.client?.id) return null;
                return { endpoint: `get_location_search/${updatedData.client.id}` };
            },
            selectionFormat: 'address',
            optionFormat: { title: { field: 'address', format: (value) => value }},
            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> },
            directOptionsTitle: 'job.general.locations_of_client_label',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        { 
            type: 'searchselect', 
            name: 'location_person',
            label: 'job.general.contact_at_location_long_label',
            placeholder: 'job.general.contact_at_location_placeholder',
            objectName: 'person.general.object_name.singular',
            query: { endpoint: 'get_person_search' },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.client?.id) return null;
                return { endpoint: `get_person_search/${updatedData.client.id}` };
            },
            selectionFormat: 'name',
            optionFormat: { title: { field: 'name', format: (value) => value } },
            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> },
            dependentOn: [
                { fieldName: 'client.entity_type', notValue: 'person' },
                { fieldName: 'context', value: 'job' }
            ],
            directOptionsTitle: 'job.general.persons_of_client_label'
        },
        {
            type: 'header',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        ...(activeFeatures.includes('projects') ? [
            { 
                type: 'searchselect', 
                name: 'project',
                label: 'job.general.project_label',
                placeholder: 'job.general.project_placeholder',
                objectName: 'project.general.object_name.singular',
                query: { endpoint: 'get_project_search' },
                showOptionsOnClick: (updatedData) => {
                    if (!updatedData.client?.id) return null;
                    return { endpoint: `get_project_search/${updatedData.client.id}` };
                },
                disableAddNew: true,
                selectionFormat: 'name',
                optionFormat: {
                    title: { field: 'name', format: (value) => value },
                    subtitle: { field: 'number', format: (value) => value, optional: true},
                },
                directOptionsTitle: 'job.general.projects_of_client_label',
                dependentOn: { fieldName: 'context', value: 'job' }
            } as FieldType] : []
        ),
        {
            type: 'dropdown',
            name: 'workflow',
            label: 'job.general.type_label',
            apiRequest: [{
                endpoint: 'get_workflow_list',
                object: 'workflow',
                params: { is_active: true, connected_feature: 'job' },
                useCache: true
            }],
            disabled_selected: 'job.general.type_disabled_selected',
            activeFeature: 'job_types',
            allowNoneOption: false,
            showSearch: false,
            orderOptions: true,
            groupByField: 'division',
            formatGroupTitle: (group) => divisions.find(division => String(division.id) === group)?.name || group,
            restrictGroupToField: 'division',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'textarea',
            name: 'main_description',
            label: 'job.general.main_description_label',
            rows: 3,
            placeholder: 'job.general.main_description_placeholder',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'header',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'date',
            name: 'appointment_date',
            label: 'job.schedule_job.date_label',
            ...(selectedDate ? { defaultValue: selectedDate } : {}),
            width: '40%',
            group: 'field-row',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'time',
            name: 'appointment_start_time',
            label: 'job.schedule_job.start_time_label',
            ...(selectedStartTime ? { defaultValue: selectedStartTime } : {}),
            width: '30%',
            group: 'field-row',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'time',
            name: 'appointment_end_time',
            label: 'job.schedule_job.end_time_label',
            ...(selectedEndTime ? { defaultValue: selectedEndTime } : selectedStartTime ? { defaultValue: addHourToStartTime(selectedStartTime) } : {}),
            width: '30%',
            group: 'field-row',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'multiselect',
            name: 'appointment_assignees',
            label: 'job.schedule_job.assignees_label',
            apiObject: 'user',
            apiRequest: [{
                endpoint: `get_user_list`,
                object: 'user',
                params: { is_active: true }
            }],
            disabled_selected: 'job.schedule_job.assignees_disabled_selected',
            ...(selectedResource ? { defaultValue: selectedResource } : {}),
            backendField: 'user_hash',
            allowNoneOption: false,
            optionFormat: (option) => option.full_name,
            selectionFormat: 'full_name',
            ...(workerPairOptions.length > 0 ? { extraOptions: workerPairOptions } : {}),
            pairForemanField: 'appointment_foreman',
            params: {
                is_active: true,
            },
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        // Only offer the equipment when it can be scheduled in this environment
        ...(equipmentSchedulingActive ? [{
            type: 'multiselect',
            name: 'appointment_equipment',
            label: 'job.schedule_job.equipment_label',
            apiObject: 'equipment',
            apiRequest: [{
                endpoint: 'get_equipment_list',
                object: 'equipment',
                params: { is_active: true }
            }],
            disabled_selected: 'job.schedule_job.equipment_disabled_selected',
            ...(selectedEquipment ? { defaultValue: selectedEquipment } : {}),
            allowNoneOption: false,
            optionFormat: (option: any) => option.type_name ? `${option.name} (${option.type_name})` : option.name,
            selectionFormat: 'name',
            params: {
                is_active: true,
            },
            dependentOn: { fieldName: 'context', value: 'job' },
        } as FieldType] : []),
        ...(scheduling_appointment_colors === "custom" ? [
            { 
                name: 'appointment_custom_color',
                label: 'job.schedule_job.color_label',
                type: 'tabs',
                showIconOnly: true,
                customClass: "label",
                options: [
                    { value: "soft-dark-blue", name: "soft-dark-blue" },
                    { value: "soft-blue", name: "soft-blue" },
                    { value: "soft-dark-green", name: "soft-dark-green" },
                    { value: "soft-green", name: "soft-green" },
                    { value: "soft-yellow", name: "soft-yellow" },
                    { value: "soft-orange", name: "soft-orange" },
                    { value: "soft-red", name: "soft-red" },
                    { value: "soft-gray", name: "soft-gray" },
                    { value: "soft-dark-gray", name: "soft-dark-gray" },
                    { value: "soft-purple", name: "soft-purple" },
                ],
                dependentOn: { fieldName: 'context', value: 'job' },
            } as FieldType] : []
        ),
        {
            type: 'header',
            dependentOn: { fieldName: 'context', value: 'job' },
        },
        {
            type: 'dropdown',
            name: 'division',
            label: 'job.general.division_label',
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'job.general.division_disabled_selected',
            activeFeature: 'divisions',
            defaultValue: userDivision ? { value: String(userDivision) } : undefined,
            allowNoneOption: false,
            showSearch: false,
            dependentOn: { fieldName: 'context', value: 'job' },
        },
    ];

    // Configure the activity group fields
    const activityGroupFields: FieldType[] = [
        { 
            type: 'dropdown', 
            name: 'activity_group_time_type', 
            label: 'timerow.general.type_label', 
            apiRequest: [{
                endpoint: 'get_non_productive_timetype_list',
                object: 'non_productive_timetype',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'timerow.general.type_disabled_selected',
            allowNoneOption: false,
            showSearch: false,
            dependentOn: { fieldName: 'context', value: 'other' },
        },
        {
            type: 'textarea',
            name: 'activity_group_note',
            label: 'timerow.general.note_label',
            rows: 3,
            isNote: true,
            placeholder: 'timerow.general.note_placeholder',
            dependentOn: { fieldName: 'context', value: 'other' },
        },
        // {
        //     type: 'header',
        //     dependentOn: { fieldName: 'context', value: 'other' },
        // },
        {
            type: 'date',
            name: 'activity_group_start_date',
            label: 'timerow.general.start_date_label',
            ...(selectedDate ? { defaultValue: selectedDate } : {}),
            width: '40%',
            group: 'field-row',
            dependentOn: { fieldName: 'context', value: 'other' },
        },
        // {
        //     type: 'date',
        //     name: 'activity_group_end_date',
        //     label: 'timerow.general.end_date_label',
        //     width: '50%',
        //     group: 'field-row',
        //     dependentOn: { fieldName: 'context', value: 'other' },
        // },
        {
            type: 'time',
            name: 'activity_group_start_time',
            label: 'timerow.general.start_time_label',
            ...(selectedStartTime ? { defaultValue: selectedStartTime } : {}),
            width: '30%',
            group: 'field-row',
            dependentOn: { fieldName: 'context', value: 'other' },
        },
        {
            type: 'time',
            name: 'activity_group_end_time',
            label: 'timerow.general.end_time_label',
            ...(selectedEndTime ? { defaultValue: selectedEndTime } : selectedStartTime ? { defaultValue: addHourToStartTime(selectedStartTime) } : {}),
            width: '30%',
            group: 'field-row',
            dependentOn: { fieldName: 'context', value: 'other' },
        },
        {
            type: 'dropdown',
            name: 'activity_group_frequency',
            label: 'timerow.general.frequency_label',
            hardcodedOptions: [
                { value: 'not_repeated', name: 'timerow.general.frequency_not_repeated' },
                { value: 'daily', name: 'timerow.general.frequency_daily' },
                { value: 'weekly', name: 'timerow.general.frequency_weekly' },
                { value: 'monthly', name: 'timerow.general.frequency_monthly' },
                { value: 'yearly', name: 'timerow.general.frequency_yearly' },
            ],
            defaultValue: { value: 'not_repeated' },
            allowNoneOption: false,
            showSearch: false,
            dependentOn: { fieldName: 'context', value: 'other' },
        },
        {
            name: 'activity_group_weekdays',
            label: 'timerow.general.select_weekdays_label',
            tooltipText: 'timerow.general.select_weekdays_tooltipText',
            type: 'multi-tabs',
            fullWidth: true,
            defaultValue: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            dependentOn: [
                { fieldName: 'context', value: 'other' },
                { fieldName: 'activity_group_frequency', value: 'daily' },
            ],
            hardcodedOptions: [
                { value: "monday", name: "date_time.days.short.monday" },
                { value: "tuesday", name: "date_time.days.short.tuesday" },
                { value: "wednesday", name: "date_time.days.short.wednesday" },
                { value: "thursday", name: "date_time.days.short.thursday" },
                { value: "friday", name: "date_time.days.short.friday" },
                { value: "saturday", name: "date_time.days.short.saturday" },
                { value: "sunday", name: "date_time.days.short.sunday" },
            ],
        },
        {
            type: 'dropdown',
            name: 'activity_group_end_type',
            label: 'timerow.general.end_type_label',
            hardcodedOptions: [
                { value: 'never', name: 'timerow.general.end_type_never' },
                { value: 'on_date', name: 'timerow.general.end_type_on_date' },
                { value: 'after_count', name: 'timerow.general.end_type_after_count' },
            ],
            defaultValue: { value: 'never' },
            allowNoneOption: false,
            showSearch: false,
            dependentOn: [
                { fieldName: 'context', value: 'other' },
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', notValue: ['on_date', 'after_count'] },
            ],
        },
        {
            // Same field as above, but taking half the row once a date or count is revealed next to it
            type: 'dropdown',
            name: 'activity_group_end_type',
            label: 'timerow.general.end_type_label',
            hardcodedOptions: [
                { value: 'never', name: 'timerow.general.end_type_never' },
                { value: 'on_date', name: 'timerow.general.end_type_on_date' },
                { value: 'after_count', name: 'timerow.general.end_type_after_count' },
            ],
            allowNoneOption: false,
            showSearch: false,
            width: '50%',
            group: 'field-row-end',
            dependentOn: [
                { fieldName: 'context', value: 'other' },
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', value: ['on_date', 'after_count'] },
            ],
        },
        {
            type: 'date',
            name: 'activity_group_end_date',
            label: 'timerow.general.end_date_label',
            width: '50%',
            group: 'field-row-end',
            dependentOn: [
                { fieldName: 'context', value: 'other' },
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', value: 'on_date' },
            ],
        },
        {
            type: 'number',
            name: 'activity_group_occurrence_count',
            label: 'timerow.general.occurrence_count_label',
            placeholder: 'timerow.general.occurrence_count_placeholder',
            min: 1,
            width: '50%',
            group: 'field-row-end',
            dependentOn: [
                { fieldName: 'context', value: 'other' },
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', value: 'after_count' },
            ],
        },
        {
            type: 'multiselect',
            name: 'activity_group_assignees',
            label: 'timerow.general.assignees_label',
            apiObject: 'user',
            apiRequest: [{
                endpoint: `get_user_list`,
                object: 'user',
                params: { is_active: true }
            }],
            disabled_selected: 'timerow.general.assignees_disabled_selected',
            ...(selectedResource ? { defaultValue: selectedResource } : {}),
            backendField: 'user_hash',
            allowNoneOption: false,
            optionFormat: (option) => option.full_name,
            selectionFormat: 'full_name',
            params: {
                is_active: true,
            },
            dependentOn: { fieldName: 'context', value: 'other' },
        },        
        {
            name: 'activity_group_custom_color',
            label: 'timerow.general.color_label',
            type: 'tabs',
            showIconOnly: true,
            customClass: "label",
            options: SCHEDULING_COLOR_OPTIONS,
            defaultValueFromField: { fieldName: 'activity_group_time_type', dropdownObject: 'non_productive_timetype' },
            dependentOn: { fieldName: 'context', value: 'other' },
        },
    ];

    const fields: FieldType[] = [...contextField, ...jobFields, ...activityGroupFields];

    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            linkedItem={linkedItem}
            onBeforeSubmit={checkPlannedDayPasses}
            sidePanel={hasJobCreationDetails
                ? <JobCreationDetailsPanel visibleWhen={(data) => data.context !== 'other'} />
                : undefined}
        />
    );
};

export default AddJobOrActivityForm;